<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n()

defineI18nRoute({
  paths: {
    'en-CA': '/sustainability/sustainable-development',
    'fr-CA': '/developpement/developpement-durable'
  }
})

useHead({
  title: t('navigation.sustainableDevelopment.metatitle'),
  meta: [{ name: 'description', content: t('navigation.sustainableDevelopment.metadescription') }]
})

</script>
<template>
   <div class="wrapContent">
      <div class="entetePage">
         <div class="wrapper">
            <div class="filAriane">
               <span class="">
                  <NuxtLink :to="localePath('index')">{{ $t('navigation.home.name') }}</NuxtLink>
                  <span class=""> &gt; </span>
                  <span class="">{{ $t('navigation.sustainability.name') }}</span>
                  <span class=""> &gt; </span>
                  <span class="">{{ $t('navigation.sustainableDevelopment.name') }}</span>
               </span>
            </div>
            <div class="title">
               <h1><span class="">{{ $t('navigation.sustainableDevelopment.name') }}</span></h1>
            </div>
         </div>
      </div>
      <div class="contentPage">
         <div class="wrapper clearfix">
            <MenuSustainability />
            <div class="contenu right">
               <div v-if="$i18n.locale == 'en-CA'">
                  <h2>Reimagine 2030: Progress with Purpose</h2>
                  <p>We’re halfway through our Reimagine 2030 journey—and more committed than ever to leaving the planet healthier than we found it. At Kruger Products, we believe growth and sustainability can go hand in hand. Our strategy is built on four pillars that guide our actions and reflect our values: <strong>Planet Conscious</strong>, <strong>Products Empower</strong>, <strong>Employee Impact</strong>, and <strong>Community Embrace</strong>.</p>
                  <section>
                     <h2>Planet Conscious</h2>
                     <p>As a company reliant on natural resources, we embrace our responsibility to steward them wisely. Since launching Reimagine 2030, we’ve made measurable progress:</p>
                     <ul>
                        <li><strong>31% reduction* in Scope 1 & 2 GHG emissions</strong></li>
                        <li><strong>40% reduction* in water consumption</strong></li>
                        <li><strong>ISO 50001 energy management certification</strong> achieved at multiple sites</li>
                     </ul>
                  </section>
                  <section>
                     <h2>Products Empower</h2>
                     <p>We continue to innovate across our product portfolio to meet ambitious sustainability goals:</p>
                     <ul>
                        <li><strong>100% of fibres used are third-party certified</strong>, with <strong>88% FSC&reg; certified</strong></li>
                        <li><strong>6.2% reduction**</strong> in virgin plastic packaging in branded products</li>
                        <li>First Canadian tissue manufacturer to join <strong>Canada’s Plastic Pact (CPP)</strong>, contributing to a circular plastics economy through the CPP Flexibles Working Group</li>
                     </ul>
                  </section>
                  <section>
                     <h2>Employee Impact</h2>
                     <p>Our people are our greatest strength. We’re proud to foster a workplace where safety, growth, and wellbeing are prioritized:</p>
                     <ul>
                        <li><strong>81% improvement*** in Employee Health & Safety</strong></li>
                        <li>Ongoing investments in health & safety infrastructure and training</li>
                     </ul>
                  </section>
                  <section>
                     <h2>Community Embrace</h2>
                     <p>We believe in giving back to the communities we serve. Through local philanthropy and community-building initiatives, we support the health and wellbeing of our neighbours and partners.</p>
                  </section>
                  <section>
                     <h2>2025 Sustainability Highlights</h2>
                     <table>
                        <thead>
                           <tr>
                              <th>
                                 Metric
                              </th>
                              <th>
                                 Achievement
                              </th>
                           </tr>
                        </thead>
                        <tbody>
                           <tr>
                              <td>
                                 Third-party certified fibre
                              </td>
                              <td>
                                 100% with 91% being FSC&reg; certified
                              </td>
                           </tr>
                           <tr>
                              <td>
                                 Scope 1 & 2 GHG emissions
                              </td>
                              <td>
                                 31% reduction*
                              </td>
                           </tr>
                           <tr>
                              <td>
                                 Water consumption
                              </td>
                              <td>
                                 40% reduction*
                              </td>
                           </tr>
                           <tr>
                              <td>
                                 Virgin plastic packaging
                              </td>
                              <td>
                                 6.2% reduction**
                              </td>
                           </tr>
                           <tr>
                              <td>
                                 Employee health & safety
                              </td>
                              <td>
                                 81% improvement***
                              </td>
                           </tr>
                           <tr>
                              <td>
                                 FSC&reg; certified products
                              </td>
                              <td>
                                 320 products
                              </td>
                           </tr>
                           <tr>
                              <td>
                                 Industry firsts
                              </td>
                              <td>
                                 FSC&reg; certification (2011), Biomass gasification system, ISO 50001 certification
                              </td>
                           </tr>
                        </tbody>
                     </table>
                  </section>
                  <section>
                     <h2>Looking Ahead</h2>
                     <p>Our mission—<strong>making everyday life more comfortable</strong>—is at the heart of every decision we make. Reimagine 2030 is more than a strategy; it’s a commitment to our customers, our people, and the planet.</p>
                     <p>To explore our full sustainability journey, visit <a href="https://www.krugerproducts.ca/sustainability" target="_blank" rel="noopener noreferrer">krugerproducts.ca/sustainability</a></p>
                  </section>
                  <section>
                     <p>*Intensity-based vs. 2015 benchmark</p>
                     <p>**Intensity-based vs. 2020 benchmark</p>
                     <p>*** OSHA Total Incidence Rate vs. 2015 benchmark</p>
                  </section>
               </div>
               <div v-if="$i18n.locale == 'fr-CA'">
                  <h2>Reconcevoir 2030 : progression guidée par un objectif</h2>
                  <p>Nous sommes à mi-parcours de notre initiative Reconcevoir 2030, et nous sommes plus déterminés que jamais à faire de notre planète un endroit plus sain que celui que nous avons trouvé. Chez Produits Kruger, nous croyons que la croissance et le développement durable peuvent aller de pair. Notre stratégie repose sur quatre piliers qui orientent nos actions et reflètent nos valeurs : <strong>Conscience de la planète</strong>, <strong>Responsabilisation des produits</strong>, <strong>Impact pour les employés</strong> et <strong>Implication dans nos communautés</strong>.</p>
                  <section>
                     <h2>Conscience de la planète</h2>
                     <p>En tant qu’entreprise dépendante des ressources naturelles, nous assumons pleinement notre responsabilité de les gérer avec discernement. Depuis le lancement de notre initiative Reconcevoir 2030, nous avons accompli des progrès mesurables :</p>
                     <ul>
                        <li><strong>31 % réduction* des émissions de GES de portée 1 et 2</strong></li>
                        <li><strong>40 % réduction* de la consommation d’eau</strong></li>
                        <li><strong>Certification de gestion de l’énergie ISO 50001</strong> obtenue sur plusieurs sites</li>
                     </ul>
                  </section>
                  <section>
                     <h2>Responsabilisation des produits</h2>
                     <p>Nous poursuivons nos efforts d’innovation à travers l’ensemble de notre portefeuille de produits afin d’atteindre nos objectifs ambitieux en matière de développement durable :</p>
                     <ul>
                        <li><strong>100 % fibres certifiées par une tierce partie</strong>, dont <strong>88 % sont certifiées FSC&reg;</strong></li>
                        <li><strong>6,2 % réduction**</strong> de l'utilisation du plastique vierge dans les emballages des produits de marque</li>
                        <li>Premier fabricant canadien de papier tissu à adhérer au <strong>Pacte canadien sur les plastiques (PCP)</strong>, contribuant à l’économie circulaire des plastiques par l’intermédiaire du groupe de travail CPP sur les plastiques flexibles</li>
                     </ul>
                  </section>
                  <section>
                     <h2>Impact pour les employés</h2>
                     <p>Notre personnel est notre plus grande force. Nous sommes fiers de favoriser un environnement de travail où l’on accorde la priorité à la sécurité, le développement et le bien-être :</p>
                     <ul>
                        <li><strong>81 % amélioration*** de la santé et de la sécurité des employés</strong></li>
                        <li>Investissements continus dans les infrastructures de santé et sécurité ainsi que dans la formation</li>
                     </ul>
                  </section>
                  <section>
                     <h2>Implication dans nos communautés</h2>
                     <p>Nous croyons à l’importance de redonner aux communautés que nous servons. Grâce à des initiatives philanthropiques locales et des projets de mobilisation communautaire, nous soutenons la bonne santé et le bien-être de nos voisins et partenaires.</p>
                  </section>
                  <section>
                     <h2>2025 Faits saillants sur le développement durable</h2>
                     <table>
                        <thead>
                           <tr>
                              <th>Mesure</th>
                              <th>Réalisation</th>
                           </tr>
                        </thead>
                        <tbody>
                           <tr>
                              <td>
                                 Fibres certifiées par une tierce partie
                              </td>
                              <td>
                                 100 % dont 91 % sont certifiées FSC&reg;
                              </td>
                           </tr>
                           <tr>
                              <td>
                                 Émissions de GES de portée 1 et 2
                              </td>
                              <td>
                                 31 % de réduction*
                              </td>
                           </tr>
                           <tr>
                              <td>Consommation d’eau</td>
                              <td>40 % de réduction*</td>
                           </tr>
                           <tr>
                              <td>Plastique vierge dans les emballages</td>
                              <td>6,2 % de réduction**</td>
                           </tr>
                           <tr>
                              <td>
                                 Santé et la sécurité des employés
                              </td>
                              <td>81 % d’amélioration***</td>
                           </tr>
                           <tr>
                              <td>Produits certifiés FSC&reg;</td>
                              <td>320 produits</td>
                           </tr>
                           <tr>
                              <td>1er de l’industrie</td>
                              <td>Certification FSC&reg; (2011), système de gazéification de la biomasse, certification ISO 50001</td>
                           </tr>
                        </tbody>
                     </table>
                  </section>
                  <section>
                     <h2>Regard vers l’avenir</h2>
                     <p>Notre mission — <strong>pour un quotidien plus confortable</strong> — est au cœur de chacune de nos décisions. Reconcevoir 2030 est bien plus qu’une stratégie : c’est un engagement envers nos clients, nos employés et notre planète.</p>
                     <p>Pour découvrir l’ensemble de notre démarche en matière de développement durable, visitez le site <a href="http://krugerproducts.ca/fr-ca/entreprise-responsable/developpement-durable" target="_blank" rel="noopener noreferrer">krugerproducts.ca/fr-ca/entreprise-responsable/developpement-durable</a></p>
                  </section>
                  <section>
                     <p>* Basé sur l'intensité par rapport au niveau de référence de 2015</p>
                     <p>** Basé sur l'intensité par rapport au niveau de référence de 2020</p>
                     <p>*** Taux d'incidence total OSHA par rapport au niveau de référence de 2015</p>
                  </section>
               </div>
            </div>
         </div>
      </div>
   </div>
</template>
<style lang="scss" scoped>
        th, td {
          text-align:left;
          padding: 8px;
        }
</style>
